import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

const LineGraph = ({ data, lineColor }) => {
  if (!data) return null;

  const svgRef = useRef();
  const tooltipRef = useRef();
  const [containerWidth, setContainerWidth] = useState(600);

  const drawGraph = () => {
    if (!data) return;

    const margin = { top: 20, right: 30, bottom: 30, left: 50 };
    const width = containerWidth - margin.left - margin.right;
    const baseHeight = 300;
    const height = baseHeight - margin.top - margin.bottom;

    // Remove existing SVG content on re-render
    d3.select(svgRef.current).selectAll('*').remove();

    const svg = d3
      .select(svgRef.current)
      .attr('viewBox', `0 0 ${containerWidth} ${baseHeight}`)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .style('background-color', '#efefef')
      .style('border-radius', '3px')
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const tooltip = d3
      .select(tooltipRef.current)
      .style('position', 'absolute')
      .style('padding', '5px')
      .style('pointer-events', 'none')
      .style('opacity', 0);

    const focusCircle = svg
      .append('g')
      .append('circle')
      .attr('r', 5)
      .attr('stroke', lineColor)
      .attr('fill', lineColor)
      .attr('stroke-width', 2)
      .style('opacity', 0);

    // Set scales
    // find the smallest value in the data array
    const publication = Object.keys(data[0]).filter(
      (key) => key !== 'Week'
    )[0];

    const minValue = data.reduce((acc, curr) => {
      if (curr[publication] < acc) {
        acc = curr[publication];
      }
      return acc;
    }, 100);

    const x = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => {
        const dateValue = new Date(d.Week);
        return dateValue;
      }))
      .range([0, width]);

    const y = d3
      .scaleLinear()
      .domain([minValue - 5, 100])
      .range([height, 0]);

    // Define line generator
    const line = d3
      .line()
      .x((d) => x(new Date(d.Week)))
      .y((d) => y(d[publication]))
      .curve(d3.curveMonotoneX);

    svg
      .append('path')
      .datum(data) // Binds the entire dataset as one line
      .attr('class', 'line')
      .attr('d', line) // The line generator works on the whole dataset
      .style('stroke', lineColor)
      .style('stroke-width', 3)
      .style('fill', 'none');

    svg
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x).ticks(d3.timeYear.every(1)));

    svg.append('g').call(d3.axisLeft(y));

    // Overlay rectangle for capturing mouse movements
    svg
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .on('mousemove', function (event) {
        const mouse = d3.pointer(event);
        const mouseX = x.invert(mouse[0]);

        // Find the closest data point
        const bisectDate = d3.bisector((d) => new Date(d.Week)).left;
        const idx = bisectDate(data, mouseX, 1);
        const d0 = data[idx - 1];
        const d1 = data[idx];
        const d = mouseX - d0?.date > d1?.date - mouseX ? d1 : d0;

        const formatDate = d3.timeFormat('%B %d, %Y');
        // Update tooltip position and content
        tooltip
          .style('opacity', 1)
          .html(
            `${formatDate(new Date(d.Week))}<br>Public Interest: ${d[publication]}%`
          )
          .style('right', 0)
          .style('top', 0);

        // Update circle position
        focusCircle
          .attr('cx', x(new Date(d.Week)))
          .attr('cy', y(d[publication]))
          .style('opacity', 1)
          .style('stroke', lineColor);
      })
      .on('mouseout', function () {
        tooltip.style('opacity', 0);
        focusCircle.style('opacity', 0);
      });
  };

  useEffect(() => {
    setContainerWidth(svgRef.current.parentElement.offsetWidth);
    drawGraph(); // Draw graph on initial load
  }, [data, lineColor, containerWidth]);

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(svgRef.current.parentElement.offsetWidth); // Update the width
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="linegraph--container">
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef}></div>
    </div>
  );
};

export default LineGraph;
